/**
 * NOS-249: renderer-agnostic head data resolution for every public page type.
 *
 * Each resolver takes plain row data and returns a serializable descriptor
 * ({ title, description, canonicalUrl, ogType, ogImage }) that the SPA feeds
 * to <SEOHead> today and the Astro app (NOS-250/252) renders into its own
 * <head> — same inputs, identical output, no framework imports.
 *
 * Fallback chains (explicit → imported/carried-over → generated default):
 * - title:       seo_title column → legacy meta.title (pages) → "<Title> | <Org>"
 * - description: seo_description column → excerpt/meta.description →
 *                body-derived text → mission/title fallback
 * - og:image:    explicit override → cover/imported image → branded
 *                template-generated default (/api/og, see ogImageTemplate.ts)
 */

import {
  resolvePageSeoTitle,
  resolvePageSeoDescription,
  type PublicPageSeo,
} from "./pageSeo"
import { extractDescriptionFromPageSections } from "./extractMetaDescription"
import { buildDefaultOgImageUrl, resolveOgImage } from "./ogImage"
import { publicSiteOrigin } from "./siteOrigin.ts"

export interface SeoOrgInput {
  name: string | null
  slug: string
  custom_domain?: string | null
  logo_url?: string | null
  mission_statement?: string | null
}

export type SeoSections = Parameters<typeof extractDescriptionFromPageSections>[0]

export interface SeoHeadData {
  title: string
  description: string
  canonicalUrl: string
  ogType: "website" | "article" | "event"
  /** Always resolvable — falls back to the generated branded default. */
  ogImage: string
}

function truncate160(text: string): string {
  const t = text.trim()
  return t.length <= 160 ? t : `${t.slice(0, 159)}…`
}

function orgDisplayName(org: SeoOrgInput): string {
  return org.name?.trim() || "Organization"
}

export interface PageHeadInput {
  org: SeoOrgInput
  /** Page row (slug + SEO columns + meta JSONB); null while unresolved. */
  page: (PublicPageSeo & { slug?: string | null }) | null
  /** Site-relative path ("/", "/about", "/blog") for the canonical URL. */
  sitePath: string
  /** Ordered page sections for the body-derived description fallback. */
  sections?: SeoSections
}

/** Head data for a builder page (home, about, custom, listing shells). */
export function resolvePageHead(input: PageHeadInput): SeoHeadData {
  const { org, page, sitePath } = input
  const orgName = orgDisplayName(org)
  const origin = publicSiteOrigin(org)
  const path = sitePath === "" || sitePath === "/" ? "" : sitePath
  const canonicalUrl = path ? `${origin}${path.startsWith("/") ? path : `/${path}`}` : origin

  const title = page ? resolvePageSeoTitle(page, orgName) : orgName

  let description = resolvePageSeoDescription(page)
  if (!description && input.sections) {
    description = extractDescriptionFromPageSections(input.sections)
  }
  if (!description) {
    const mission = org.mission_statement?.trim()
    description = mission ? truncate160(mission) : `${orgName} — nonprofit website`
  }

  // The generated default renders the page's display title as the headline
  // (org name is already in the template footer); home uses the org name.
  const pageTitle = page?.title?.trim()
  const headline = !pageTitle || page?.slug === "home" ? orgName : pageTitle
  const meta = page?.meta ?? {}
  const explicitOg = typeof meta.og_image === "string" ? meta.og_image : null

  return {
    title,
    description,
    canonicalUrl,
    ogType: "website",
    ogImage: resolveOgImage({
      explicit: explicitOg,
      generatedDefault: buildDefaultOgImageUrl(origin, org.slug, headline),
    }),
  }
}

export interface BlogPostHeadInput {
  org: SeoOrgInput
  post: {
    title: string
    slug: string
    seo_title?: string | null
    seo_description?: string | null
    og_image?: string | null
    excerpt?: string | null
    cover_image?: string | null
  }
  sections?: SeoSections
}

/** Head data for a public blog post. */
export function resolveBlogPostHead(input: BlogPostHeadInput): SeoHeadData {
  const { org, post } = input
  const orgName = orgDisplayName(org)
  const origin = publicSiteOrigin(org)
  const canonicalUrl = `${origin}/blog/${post.slug}`

  const title = post.seo_title?.trim() || `${post.title} | ${orgName}`

  let description = post.seo_description?.trim() ?? ""
  if (!description && post.excerpt?.trim()) {
    description = truncate160(post.excerpt)
  }
  if (!description && input.sections) {
    description = extractDescriptionFromPageSections(input.sections)
  }
  if (!description) description = post.title

  return {
    title,
    description,
    canonicalUrl,
    ogType: "article",
    ogImage: resolveOgImage({
      explicit: post.og_image,
      cover: post.cover_image,
      generatedDefault: buildDefaultOgImageUrl(origin, org.slug, post.title),
    }),
  }
}

export interface EventHeadInput {
  org: SeoOrgInput
  event: {
    title: string
    slug: string
    seo_title?: string | null
    seo_description?: string | null
    og_image?: string | null
    cover_image?: string | null
  }
  sections?: SeoSections
}

/** Head data for a public event detail page. */
export function resolveEventHead(input: EventHeadInput): SeoHeadData {
  const { org, event } = input
  const orgName = orgDisplayName(org)
  const origin = publicSiteOrigin(org)
  const canonicalUrl = `${origin}/events/${event.slug}`

  const title = event.seo_title?.trim() || `${event.title} | ${orgName}`

  let description = event.seo_description?.trim() ?? ""
  if (!description && input.sections) {
    description = extractDescriptionFromPageSections(input.sections)
  }
  if (!description) description = `${event.title} — ${orgName}`

  return {
    title,
    description,
    canonicalUrl,
    ogType: "event",
    ogImage: resolveOgImage({
      explicit: event.og_image,
      cover: event.cover_image,
      generatedDefault: buildDefaultOgImageUrl(origin, org.slug, event.title),
    }),
  }
}
